/**
 * @fileoverview Order notes details component for comprehensive note management.
 * Provides detailed view and editing capabilities for order notes including system notes,
 * user notes, and pinned notes with full CRUD operations.
 *
 * @author Web Analysis Team
 * @version 1.0.0
 */

/* eslint-disable no-unused-vars */
/* eslint-disable eqeqeq */
import { Box } from '@mui/system';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import AgGrid from '../../../../components/ag-grid/ag-grid';
import { Button, Checkbox, FormControlLabel, IconButton, LinearProgress, TextField, Tooltip, Typography, InputAdornment } from '@mui/material';
import { Add, Remove, Lock } from '@mui/icons-material';
import { dayjs as mydayjs, toLocalTime } from '../../../../components/ex_dayjs';
import { useNotify, useStore } from 'react-admin';
import { OrderContext } from '../hooks/OrderContext';
import { IData } from '../../../../types';

/**
 * Unique identifier for lock row in the notes grid.
 * @type {string}
 * @constant
 */
const lockId = 'lock';

/**
 * Default pinned top row configuration for the notes grid.
 * @type {Array<Object>}
 * @constant
 */
const defaultPinTop = [
    { id: lockId, post: 'Pinned Notes', empty: null, pinTag: null }
];

/**
 * Maximum height for pinned rows section in pixels.
 * @type {number}
 * @constant
 */
const maxHeightPinnedRows = 250;

/**
 * Order notes details component for comprehensive note management.
 * Provides full editing capabilities, system note filtering, and customer information display.
 *
 * @param {Object} props - Component properties
 * @param {Object} props.dataProvider - Data provider for API operations
 * @param {Object} props.vendor - Vendor configuration object
 * @param {Function} props.callback - Callback function for note operations
 * @param {Array} props.notes - Array of note objects
 * @param {Object} props.customer - Customer information object
 * @param {Object} props.order - Order information object
 * @param {boolean} [props.showCustomerInfo=true] - Controls visibility of customer info for DT/Flash orders
 * @param {string} props.convertNote - Note conversion parameters
 * @param {Function} props.loadNotes - Function to reload notes
 * @returns {JSX.Element} Comprehensive notes management interface
 */
const OrderNotesDetails: React.FC<IData> = (props) => {
    let { dataProvider, vendor, callback, notes, customer, order, convertNote, loadNotes, showCustomerInfo = true } = props;
    const { showSystemNotes, setShowSystemNotes } = useContext(OrderContext);
    const [pinnedTopRowData, setPinnedTopRowData] = useState<IData[]>(defaultPinTop);
    // const [newNote, setNewNote] = useState<{ value: string; require: string | null }>({ value: '', require: null });
    const [realHeight, setRealHeight] = useState(0);
    const notify = useNotify();
    // Store key for persisting draft note state in local storage
    const storeKey = `${vendor.controller}.draft_note.${order.id}`;
    const [draftNote, setDraftNote] = useStore(storeKey, '');
    const [noteError, setNoteError] = useState<string | null>(null);

    useEffect(() => {
        if (notes && order) {
            //-1(System) & -99 (User)
            let listUnpinned = notes.filter((x: any) => x.note_type < 0);
            if (listUnpinned.length) {
                listUnpinned = listUnpinned.map((x: any) => ({ ...x, id: '***' }));
            }
            let newPinnedTopRows = [...defaultPinTop, ...listUnpinned];
            // reduce duplicates in pinned top rows
            newPinnedTopRows = newPinnedTopRows.filter((item: any, index: number, self: any[]) =>
                index === self.findIndex((t) => (
                    t.post === item.post && t.note_type === item.note_type
                ))
            );

            setPinnedTopRowData(newPinnedTopRows);
        }
    }, [notes, order, customer, showCustomerInfo]);

    useEffect(() => {
        if (realHeight !== 0) {
            setStylePinnedRows(realHeight);
        }
    }, [realHeight]);

    useEffect(() => {
        if (convertNote.isSaveNote && convertNote.newNote) {
            handleAddNote(convertNote.newNote);
        }
    }, [convertNote.isSaveNote]);

    const postComponent = (props: IData) => {
        const { data, value } = props;
        return (<span style={{ whiteSpace: 'wrap', overflowWrap: 'anywhere' }}>
            <strong>{(vendor.prettifyAuthorName(data?.author))}:&nbsp;</strong>
            {value} -&nbsp;
            <Tooltip title={toLocalTime(data?.utc_posted_on_str).format('MMM DD, YYYY hh:mm A')}>
                <span style={{ color: '#1a76d2' }}>{mydayjs.utc(data?.utc_posted_on_str).fromNow()}</span>
            </Tooltip>
        </span>);
    };
    const systemNotesComponent = (props: IData) => {
        return (
            <FormControlLabel
                sx={{ marginLeft: '55px' }}
                control={<Checkbox onChange={handleShowSystemNote} checked={showSystemNotes} />}
                label={
                    <Typography sx={{ fontSize: '10px', fontWeight: 'bold' }}>Show System Notes Also</Typography>
                }
            />
        );
    };
    const pinNoteComponent = (props: IData) => {
        const { data } = props;
        let pinned = data.note_type >= 0;
        const onPin = () => {
            //Pin: UserNote - (-99), SystemNote - (-1)
            //Unify note_type (0 | -99: UserNote)
            let noteType = parseInt(data.note_type);
            if (noteType === -99) noteType = 0;
            else if (noteType === 0) noteType = 99;

            dataProvider
                .postData(vendor.controller, 'Note', {
                    note_id: data.note_id,
                    order_id: order.id,
                    order_number: order.order_number,
                    note_type: -1 * noteType
                })
                .then((rs: any) => {
                    if (rs.data) {
                        callback(rs.data);
                        notify('Your note for order #' + order.order_number + (noteType > 0 ? ' pinned' : ' unpinned'), { type: 'success', autoHideDuration: 2000 });
                    }
                })
                .catch((error: any) => {
                    notify(`Add note failed: Status ${error.status}, Error ${error}`, { type: 'error', autoHideDuration: 3000 });
                });
        };
        return (
            <>
                {data.id !== lockId ?
                    <IconButton color="primary" title={pinned ? 'Pin' : 'Unpin'} onClick={onPin}> {pinned ? <Add /> : <Remove style={{ color: 'red' }} />}</IconButton>
                    : <></>
                }
            </>
        );
    };

    const handleShowSystemNote = (props: IData) => {
        setShowSystemNotes(props.target.checked);
        loadNotes();
    };

    function handleAddNote (noteValue: IData | string) {
        let note = typeof noteValue === 'string' ? noteValue : draftNote.trim();
        if (!note) {
            setNoteError('This field cannot be null or contain only whitespace.');
            return;
        }

        dataProvider
            .postData(vendor.controller, 'Note', {
                order_id: order.id,
                order_number: order.order_number,
                post: note
            })
            .then((rs: any) => {
                if (rs.data) {
                    callback(rs.data);
                    notify('Your note for order #' + order.order_number + ' added.', { type: 'success', autoHideDuration: 2000 });
                }
                // IMPORTANT: Clear the draft note from local storage after successful addition
                setDraftNote('');
            })
            .catch((error: any) => {
                notify(`Add note failed: Status ${error.status}, Error ${error}`, { type: 'error', autoHideDuration: 3000 });
            })
            .finally(() => setNoteError(null));
    };

    //declare columns in aggrid
    const columnDefs = [
        {
            field: 'id', headerName: '#', width: 30, cellClass: 'id-field-aggrid', headerClass: 'id-field-aggrid',
            cellRendererSelector: (params: IData) => {
                if (params.node.rowPinned && params.node.rowTop === 0) {
                    if (params.node.rowPinned === 'top') {
                        if (params.node.data.id === lockId) return renderLock();
                    } else return { component: () => (<span style={{ fontSize: 'large' }}>$</span>) };
                } else {
                    if (params.node.data.id === lockId) return renderLock();
                    // rows that are not pinned don't use any cell renderer
                    return undefined;
                }

                function renderLock () {
                    return {
                        component: () => <Lock style={{ fontWeight: 'bold', textAlign: 'left', verticalAlign: 'middle', color: '#1a76d2' }} />
                    };
                }
            }
        },
        {
            field: 'post', headerName: 'Note', flex: 1, autoHeight: true, colSpan: () => 2,
            cellStyle: { whiteSpace: 'normal', wordWrap: 'break-word', width: '93%' },
            cellRenderer: postComponent,
            cellRendererSelector: (params: IData) => {
                if (params.node.rowPinned && params.node.rowTop === 0) {
                    if (params.node.rowPinned === 'top') {
                        return renderPinnedTop(params.node.data.id === lockId ? null : { color: 'red' });
                    }
                } else {
                    if (params.node.data.id === lockId) return renderPinnedTop();
                    return undefined;
                }

                function renderPinnedTop (style: IData | null = {}) {
                    return {
                        component: () => {
                            let newStyle = { fontWeight: 'bold', color: '#1a76d2', overflow: 'hidden', ...style };
                            if (Array.isArray(params.node.data.post)) {
                                let length = params.node.data.post.length;
                                return (
                                    <>
                                        {params.node.data.post.map((p: any, index: number) => (
                                            <span key={index} style={{ ...newStyle, float: index === length - 1 ? 'right' : 'left' }}>
                                                {p}
                                            </span>
                                        ))}
                                    </>
                                );
                            }
                            return <span style={newStyle}>{params.node.data.post}</span>;
                        }
                    };
                }
            }
        },
        {
            field: 'empty', width: 250, type: 'rightAligned', headerComponent: systemNotesComponent,
            cellRendererSelector: (params: IData) => {
                if (params.node.rowPinned && params.node.rowTop === 0) {
                    return { component: () => null };
                } else return undefined;
            }
        },
        {
            field: 'pintag', headerName: '', width: 40, cellStyle: { textAlign: 'left' },
            cellRenderer: pinNoteComponent,
            cellRendererSelector: (params: IData) => {
                if (params.node.rowPinned && params.node.rowTop === 0) {
                    return { component: () => null };
                } else {
                    return undefined;
                }
            }
        }
    ];
    const defaultColDef = useMemo(() => {
        return {
            cellStyle: { 'fontSize': '14px', 'alignItem': 'center' },
        };
    }, []);

    let gridOptions = {
        getRowStyle: ({ node }: IData) => {

            if (node.rowPinned === 'top') {
                if (node.data.note_type < 0) {
                    return {
                        color: 'red',
                        backgroundColor: '#f0f0c9' //'lightgoldenrodyellow' for light mode
                    };
                }
            }
            return 0;
        },
        getRowHeight: (params: IData) => {
            return calculateHeight(params.node, params.data);
        },
        onFirstDataRendered: (params: IData) => {
            setTimeout(() => {
                const api = params.api;
                const pinnedRows = api.getPinnedTopRowCount();
                let totalHeight = 0;

                for (let i = 0; i < pinnedRows; i++) {
                    const rowNode = api.getPinnedTopRow(i);
                    const rowHeight = calculateHeight(rowNode, rowNode.data);
                    totalHeight += rowHeight;
                }
                totalHeight = totalHeight > maxHeightPinnedRows ? maxHeightPinnedRows : totalHeight;
                setRealHeight(totalHeight);
            }, 2000);
        }
    };

    function calculateHeight (node: IData, data: IData) {
        const baseHeight = 42;
        if (node.rowPinned === 'top' && data.note_type < 0) {
            const contentLength = data.post.length;
            // each 50 chars add xx-px into height,
            return baseHeight + Math.ceil(contentLength / 50) * 11.4; //row height for pinned top row
        }
        else return baseHeight;
    }

    function setStylePinnedRows (realHeight: number) {
        // Apply the height to pinned top container
        let viewport = document.querySelector<HTMLElement>('#order-page .Form-Order .order-notes .ag-floating-top-viewport');
        if (viewport) {
            // viewport.style.setProperty('overflow-y', 'auto', 'important');
            viewport.style.setProperty('overflow', 'unset', 'important');
            viewport.style.setProperty('max-height', `${maxHeightPinnedRows}px`, 'important');
        }

        let floatingTop = document.querySelector<HTMLElement>('#order-page .Form-Order .order-notes .ag-floating-top');
        if (floatingTop) {
            floatingTop.style.setProperty('min-height', `${realHeight}px`, 'important');
            floatingTop.style.setProperty('max-height', `${realHeight}px`, 'important');
        }
    }

    return (
        <Box sx={{ height: '100%', width: '100%', border: '1px solid #bbbfc7' }} className="notes-box">
            {notes == null && <LinearProgress color='secondary' />}
            {pinnedTopRowData &&
                (<Box sx={{ height: '88%' }}>
                    <AgGrid //https://www.ag-grid.com/javascript-data-grid/grid-options
                        className='order-notes'
                        applyTransaction
                        columnDefs={columnDefs}
                        rowData={(showSystemNotes ? notes?.filter((x: any) => x.note_type >= 0) : notes?.filter((x: any) => x.note_type >= 0 && x.note_type !== 1))?.map((x: any, index: number) => ({ ...x, id: index + 1 }))}
                        defaultColDef={defaultColDef}
                        gridHeight={'100%'}
                        hidePaging={true}
                        rowSelection={'single'}
                        gridOptions={gridOptions}
                        pinnedTopRowData={pinnedTopRowData}
                    />
                    <TextField sx={{ marginTop: '7px', marginLeft: '4px', marginBottom: '8px', width: '88%' }}
                        variant='outlined'
                        placeholder="New note for this order" multiline rows={3}
                        slotProps={{
                            input: { startAdornment: <InputAdornment position="start">$</InputAdornment>, sx: { padding: '0px 0px 0px 14px' } },
                        }}
                        value={draftNote}
                        onChange={(ev) => setDraftNote(ev.target.value)}
                        error={Boolean(noteError && !draftNote)}
                    />
                    <Button variant="outlined" size="small" sx={{ textTransform: 'capitalize', minWidth: '50px', marginTop: '23px', borderTopLeftRadius: '0px', borderBottomLeftRadius: '0px', borderLeft: '0px', borderColor: '#c4c4c4' }} onClick={(param) => handleAddNote(param)}>Add</Button>
                </Box>)}
        </Box>
    );
};
export default OrderNotesDetails;